import React, { useEffect, useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/integrations/supabase/client';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { format } from 'date-fns';

interface PromosAnalyticsData {
  id: string;
  ad_name: string | null;
  offer_text: string | null;
  company_name: string;
  impressions: number;
  clicks: number;
  views: number;
  redemptions: number;
  created_at: string;
  end_date: string;
  is_active: boolean;
}

const AdminPromosAnalytics: React.FC = () => {
  const { user } = useAuth();
  const [promosData, setPromosData] = useState<PromosAnalyticsData[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchPromosAnalytics = async () => {
      if (!user) return;

      try {
        const { data, error } = await supabase
          .from('ads')
          .select(`
            id,
            ad_name,
            offer_text,
            company_name,
            impressions,
            clicks,
            views,
            redemptions,
            created_at,
            end_date,
            is_active
          `)
          .eq('is_test', false)
          .order('created_at', { ascending: false });

        if (error) throw error;
        setPromosData(data || []);
      } catch (error) {
        console.error('Error fetching promos analytics:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchPromosAnalytics();
  }, [user]);

  const formatDate = (dateString: string) => {
    return format(new Date(dateString), 'dd/MM/yyyy');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="container mx-auto py-6 space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-3xl font-bold">Analytics de Promociones</h1>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Rendimiento de Promociones</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="rounded-md border">
            <Table>
               <TableHeader>
                <TableRow>
                  <TableHead>Promo</TableHead>
                  <TableHead>Negocio</TableHead>
                  <TableHead className="text-center">Vistas App</TableHead>
                  <TableHead className="text-center">Clicks App</TableHead>
                  <TableHead className="text-center">Redenciones</TableHead>
                  <TableHead className="text-center">Impresiones</TableHead>
                  <TableHead className="text-center">CTR</TableHead>
                  <TableHead>Estado</TableHead>
                  <TableHead>Creado</TableHead>
                  <TableHead>Expira</TableHead>
                </TableRow>
              </TableHeader>
               <TableBody>
                {promosData.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={10} className="text-center py-8 text-muted-foreground">
                      No hay datos de promociones disponibles
                    </TableCell>
                  </TableRow>
                ) : (
                  promosData.map((promo) => {
                    const ctr = promo.impressions > 0 
                      ? ((promo.clicks / promo.impressions) * 100).toFixed(2)
                      : '0.00';
                    
                    return (
                      <TableRow key={promo.id}>
                        <TableCell className="font-medium">
                          {promo.ad_name || promo.offer_text || 'Sin título'}
                        </TableCell>
                        <TableCell>{promo.company_name}</TableCell>
                        <TableCell className="text-center">{promo.views || 0}</TableCell>
                        <TableCell className="text-center">{promo.clicks || 0}</TableCell>
                        <TableCell className="text-center">{promo.redemptions || 0}</TableCell>
                        <TableCell className="text-center">{promo.impressions || 0}</TableCell>
                        <TableCell className="text-center">{ctr}%</TableCell>
                        <TableCell>
                          <Badge variant={promo.is_active ? "default" : "secondary"}>
                            {promo.is_active ? 'Activa' : 'Inactiva'}
                          </Badge>
                        </TableCell>
                        <TableCell>{formatDate(promo.created_at)}</TableCell>
                        <TableCell>{formatDate(promo.end_date)}</TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default AdminPromosAnalytics;